import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { isValidEmail } from '../../utils/utils';
import * as SessionActions from '../../stores/actions/session';
import { APP, ONBOARDING_ROUTES } from '../../stores/session-status';
import TextInput from '../general-components/text-input';
import LoadingSpinner from '../general-components/loading-spinner';
import type { SessionState } from '../../stores/types/session';

interface SignInProps {
	host?: boolean;
}

interface StateProps {
	SessionReducer: SessionState;
}

interface DispatchProps {
	forgotPasswordEvents: Function;
	forgotPasswordGreenroom: Function;
	setOnboardingRoute: Function;
}

const mapStateToProps = (state: any, ownProps: any) => ({ ...state, ...ownProps });

const mapDispatchToProps = (dispatch: Function) =>
	// the actions you want to fire from this component
	 ({
		forgotPasswordEvents: (email: string) => {
			dispatch(SessionActions.forgotPasswordEvents(email));
		},
		forgotPasswordGreenroom: (email: string, isProducer: boolean) => {
			dispatch(SessionActions.forgotPasswordGreenroom(email, isProducer));
		},
		setOnboardingRoute: (route: number) => {
			dispatch(SessionActions.setOnboardingRoute(route));
		},
	});
type Props = StateProps & DispatchProps & SignInProps;

// NOTE: There is a difference between Events producers and presenters. If a producer has logged in with Events and then goes through the update
// password flow, their BL Events account password is updated. If a presenter has logged in with Events and then goes through the update password flow, they are actually creating
// a standalone GR password now and when they use that new password to log in moving forward their account is a standalone GR account and profile
// data doesn't flow back and forth between GR <-> BL Events now that it is separated. (But if they log in with the Events password they forgot, we
// would load the events profile)

function ForgotPassword(props: Props) {
	const {
		SessionReducer: {
			forgotPasswordLoading,
			forgotPasswordError,
			forgotPasswordSuccess,
			selectedApp,
			emailEntered,
		},
		forgotPasswordEvents,
		forgotPasswordGreenroom,
		setOnboardingRoute,
		host,
	} = props;

	const [email, setEmail] = useState<string>('');
	const [emailValid, setEmailValid] = useState(false);
	const [error, setError] = useState<string>('');

	useEffect(() => {
		// If user is a producer, prefill the email input with the email the producer entered
		// at the initial "Enter Email" page before selecting the app.
		if (host && emailEntered) {
			setEmail(emailEntered);
			setEmailValid(true);
		}
	}, [host, emailEntered]);

	function submitForm(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();

		if (!emailValid) return setError('Please enter a valid email.');
		setError('');

		// Events and Standalone GR are the only apps that we handle forgot password flow from within GR. Showrooms and AllHands are handled
		// within their respective apps.
		if (selectedApp === APP.EVENTS_LEGACY) {
			forgotPasswordEvents(email);
		} else {
			forgotPasswordGreenroom(email, host);
		}

		setEmail('');
	}

	function checkEmail(e: React.ChangeEvent<HTMLInputElement>) {
		setEmail(e.target.value);
		setEmailValid(isValidEmail(e.target.value));
	}

	function isFormValid() {
		return !!email;
	}

	function backToSignIn() {
		setOnboardingRoute(host ? ONBOARDING_ROUTES.PRODUCER_LOGIN : ONBOARDING_ROUTES.PRESENTER_LOGIN);
	}

	return (
		<div className={classNames('panel', !host ? 'teal-green sign-in' : 'producer sign-in')}>
			{!forgotPasswordSuccess ? (
				<form onSubmit={submitForm}>
					<div className="presenter-message-container">
						<h2>Please enter your email</h2>
					</div>
					<TextInput
						onChange={checkEmail}
						placeholder={'Email'}
						value={email}
						type={'email'}
						required
					/>

					<button className="onboarding" disabled={!isFormValid()}>Send Password Reset Link{forgotPasswordLoading && (
						<div style={{ width: 14, height: 14, marginLeft: 8 }}>
							<LoadingSpinner
								height={14}
								width={14}
								stroke={10}
							/>
						</div>
					)}</button>

					<div className="create-account">
						<span onClick={backToSignIn} className="forgot-password clickable">Back to log in</span>
					</div>

					{forgotPasswordError && (
						<div className="signin-error">
							<p>{forgotPasswordError}</p>
						</div>
					)}

					<div className={classNames('signin-error', { show: !!error })}>
						<p>{error}</p>
					</div>
				</form>
			) : (
				<>
					<div>
						<h2>A password reset link has been sent to the email you provided.</h2>
					</div>

					<div className="create-account">
						<span><span onClick={backToSignIn} className="clickable">Back to log in</span></span>
					</div>
				</>
			)}

		</div>
	);
}

export default connect<StateProps, DispatchProps, SignInProps>(mapStateToProps, mapDispatchToProps)(ForgotPassword);
